import { IconClose } from "@/assets/icons";
import { useFormField } from "@/components/elements/FormField/FormField";
import { IconBox } from "@/components/elements/IconBox";
import { useScrollLockOnOpen } from "@/hooks/useScrollLockOnOpen";
import { background, blue, divider, lightBlue, state, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { Box, css, InputAdornment, styled } from "@mui/material";
import { TimePicker, TimePickerProps, PickerChangeHandlerContext, TimeValidationError } from "@mui/x-date-pickers";
import { Dayjs, isDayjs } from "dayjs";
import { forwardRef, FC, useMemo, useRef, useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { dayjs } from "@/utils/dayjs";

export type TInputTimePickerProps = {
  readonly?: boolean;
  previousValue?: Dayjs;
  nonControl?: boolean;
  previous?: boolean;
  placeholder?: string;
  error?: boolean;
  format?: string;
  enableCloseIcon?: boolean;
  onBlur?: (e?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
} & Omit<TimePickerProps<Dayjs>, "size" | "readOnly">;

export const InputTimePicker = forwardRef<FC, TInputTimePickerProps>(
  (
    {
      readonly = false,
      previousValue,
      placeholder = "選択してください",
      nonControl = false,
      error,
      format = "HH:mm",
      enableCloseIcon = true,
      ...rest
    },
    _ref,
  ) => {
    const { field, fieldState } = useFormField(nonControl);
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useScrollLockOnOpen(open, wrapperRef);

    const value = useMemo(() => {
      if (isDayjs(field?.value)) return field.value;
      if (field?.value) return dayjs(field.value, "HH:mm:ss");
      if (isDayjs(rest.value)) return rest.value;
      if (rest.value) return dayjs(rest.value, "HH:mm:ss");
      return null;
    }, [field?.value, rest.value]);

    const clearValue = () => {
      field?.onChange(null);
      rest.onChange?.(null, {
        validationError: null,
      });
      field?.onBlur();
    };

    const [changed, setChanged] = useState(false);

    const handleChange = (newValue: Dayjs | null, context: PickerChangeHandlerContext<TimeValidationError>) => {
      const formatted = newValue ? newValue.format("HH:mm:ss") : null;
      field?.onChange(formatted);
      rest.onChange?.(newValue, context);
      setChanged(true);
    };

    const isPrevious = useMemo(() => {
      if (fieldState?.isDirty === false) return true;
      const curValue = value;
      if (isDayjs(curValue) && isDayjs(previousValue)) {
        return curValue.format(format) == previousValue?.format(format);
      }
      return false;
    }, [fieldState?.isDirty, previousValue, field?.value, rest.value]);
   
    const handleOpen = ()=>{
      !readonly && !rest.disabled && setOpen(true)
    }
    const handleInputChange =()=>{
      if (changed) {
        field?.onBlur();
        rest.onBlur?.();
        setChanged(false);
      }
      return
    }
    const handleAcceptClock =()=>{
      field?.onBlur?.();
      rest.onBlur?.();
    }
    const handleCloseClock =()=>{
      setOpen(false);
      field?.onBlur?.();
      rest.onBlur?.();
    }

    return (
      <InputTimePickerWrap ref={wrapperRef} previous={isPrevious}>
        {isDayjs(value) && !rest.disabled && !readonly && enableCloseIcon && (
          <ClearIcon onClick={clearValue}>
            <IconClose fontSize="20px" color={text.primary} />
          </ClearIcon>
        )}
        <TimePicker
          slotProps={{
            textField: {
              placeholder,
              error: Boolean(fieldState?.error) || error,
              onBlur: handleInputChange,
              InputProps: {
                startAdornment: (
                  <InputAdornment
                    position="start"
                    onClick={handleOpen}
                    style={{ cursor: readonly || rest.disabled ? "default" : "pointer" }}
                  >
                    <AccessTimeIcon sx={{ color: "#808080", fontSize: "18px" }} />
                  </InputAdornment>
                ),
              },
            },
          }}
          format={format}
          open={open}
          onAccept={handleAcceptClock}
          onClose={handleCloseClock}
          disableOpenPicker
          closeOnSelect
          readOnly={readonly}
          {...rest}
          {...field}
          value={value ?? null}
          onChange={handleChange}
        />
      </InputTimePickerWrap>
    );
  },
);

const options = { shouldForwardProp: (propName: string) => !["previous"].includes(propName) };

const InputPointer = styled(Box)`
  position: absolute;
  top: 0;
  left: 0%;
  width: 100%;
  height: 100%;
  z-index: 2;
  cursor: pointer;
`;

const InputTimePickerWrap = styled(Box, options)<{ previous: boolean }>`
  position: relative;
  .MuiTextField-root {
    width: 100%;
  }
  .MuiInputBase-root {
    background: ${background.white};
    padding: 6px;
    width: 100%;
    border-radius: ${rounded.xs};
    input {
      padding: 0;
      color: ${text.primary};
      caret-color: ${blue[70]};
      ${css(typography.body14)}
      &::placeholder {
        opacity: 1;
        color: ${text.tertiary}!important;
      }
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.xs};
      border-width: 1px !important;
    }

    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-error {
      background: ${background.error};
      fieldset {
        border-color: ${state.error_1}!important;
      }
    }
    &.Mui-disabled {
      background: ${background.disable};
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
    &.Mui-readOnly {
      background: ${background.disable};
      pointer-events: none;
      input {
        color: ${text.primary}!important;
        &::placeholder {
          opacity: 0;
        }
      }
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
    ${({ previous }) => previous && PreviousInputTimePicker};
  }
`;

const PreviousInputTimePicker = css`
  input {
    color: ${lightBlue[60]};
  }
`;

const ClearIcon = styled(IconBox)`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 2;
`;
